// CarGurus REAL Upload Service
// This is how CarGurus actually accepts inventory

import { supabase } from '../lib/supabase';

class CarGurusRealUploadService {
  constructor() {
    // CarGurus ACTUAL Integration Methods
    this.config = {
      dealerId: 'paradise-performance',
      
      // Method 1: FTP Upload (Most Common)
      ftpHost: 'ftp.cargurus.com',
      ftpUser: 'paradise-ds', // From your email
      ftpPassword: '', // You need to get this from CarGurus
      
      // Method 2: Homenet/DealerSocket Integration
      homenetUrl: 'https://www.homenetiol.com/cargurus/',
      
      // Method 3: Direct File Upload URL
      uploadUrl: 'https://wwws.cargurus.com/dealer_admin/inventory_feed',
      
      // Your API Credentials (for stats, not uploads)
      statsAppId: 'acbbc8e9-fcfd-4bae-ac9a-2e1a2f1b0de2',
      statsToken: '33a2e213-3480-4f29-bca2-33d073b46405'
    };
  }

  // Fetch vehicles from database
  async fetchVehicles() {
    try {
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('is_active', true)
        .neq('status', 'sold');  // Changed from multiple conditions to just exclude sold

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      return [];
    }
  }

  // Generate CarGurus CSV Feed (Their preferred format)
  generateCarGurusCSV(vehicles) {
    // CarGurus EXACT CSV format
    const headers = [
      'vin',
      'stock_number',
      'year',
      'make',
      'model',
      'trim',
      'body_style',
      'doors',
      'exterior_color',
      'interior_color',
      'engine',
      'transmission',
      'drivetrain',
      'mileage',
      'price',
      'description',
      'image_urls',
      'professional',
      'new_used'
    ];
    
    const rows = vehicles.map(v => {
      // Format images as pipe-separated URLs
      const imageUrls = (v.images || []).map(img => img.url || img).join('|');
      
      return [
        v.vin || '',
        v.stock_number || v.id,
        v.year || '',
        v.make || '',
        v.model || '',
        v.trim || '',
        v.body_type || '',
        v.doors || '4',
        v.exterior_color || '',
        v.interior_color || '',
        v.engine || '',
        v.transmission || 'Automatic',
        v.drivetrain || '',
        v.mileage || '0',
        v.price || '0',
        (v.description || '').replace(/[\n\r,]/g, ' '), // Remove commas and newlines
        imageUrls,
        v.professional ? 'Y' : 'N',
        v.condition === 'new' ? 'N' : 'U'
      ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');
    });
    
    return headers.join(',') + '\n' + rows.join('\n');
  }

  // Generate feed URL for CarGurus to fetch
  async generateFeedUrl() {
    try {
      // Create a public URL that CarGurus can access
      const vehicles = await this.fetchVehicles();
      const csv = this.generateCarGurusCSV(vehicles);
      
      // Store the CSV in Supabase storage temporarily
      const fileName = `cargurus-feed-${Date.now()}.csv`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('public')
        .upload(`feeds/${fileName}`, csv, {
          contentType: 'text/csv',
          upsert: true
        });
      
      if (uploadError) throw uploadError;
      
      // Get public URL
      const { data: urlData } = supabase.storage
        .from('public')
        .getPublicUrl(`feeds/${fileName}`);
      
      return {
        success: true,
        feedUrl: urlData.publicUrl,
        vehicleCount: vehicles.length,
        instructions: this.getUploadInstructions(urlData.publicUrl)
      };
      
    } catch (error) {
      console.error('Feed generation error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get instructions for manual upload
  getUploadInstructions(feedUrl) {
    return {
      method1_automatic: {
        title: 'Automatic Feed Setup (Recommended)',
        steps: [
          '1. Log into CarGurus Dealer Portal: https://wwws.cargurus.com/dealer_admin/',
          '2. Go to "Inventory" > "Inventory Feed"',
          '3. Select "URL Feed" option',
          `4. Enter this feed URL: ${feedUrl}`,
          '5. Set update frequency to "Daily"',
          '6. Click "Save and Test Feed"'
        ]
      },
      method2_ftp: {
        title: 'FTP Upload',
        steps: [
          '1. Download the CSV file from the admin panel',
          '2. Use an FTP client (like FileZilla)',
          '3. Connect to: ftp.cargurus.com',
          '4. Username: paradise-ds',
          '5. Password: (get from CarGurus support)',
          '6. Upload the CSV file to the root directory'
        ]
      },
      method3_manual: {
        title: 'Manual Upload',
        steps: [
          '1. Download the CSV file from the admin panel',
          '2. Log into CarGurus Dealer Portal',
          '3. Go to "Inventory" > "Upload Inventory"',
          '4. Select the CSV file',
          '5. Click "Upload"'
        ]
      },
      support: {
        title: 'Need Help?',
        contact: 'Stan Franson - support@cargurus.com - 800-227-4878',
        note: 'Ask them to enable automatic feed pulling from your URL'
      }
    };
  }

  // Download CSV for manual upload
  async downloadCSV() {
    const vehicles = await this.fetchVehicles();
    const csv = this.generateCarGurusCSV(vehicles);
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cargurus-inventory-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    return {
      success: true,
      vehicleCount: vehicles.length,
      message: 'CSV downloaded successfully'
    };
  }

  // Log sync attempt
  async logSync(method, vehicleCount, status, details = {}) {
    try {
      await supabase.from('cargurus_sync_logs').insert({
        upload_id: `${method}-${Date.now()}`,
        vehicle_count: vehicleCount,
        status,
        error_message: details.error || null,
        synced_at: new Date().toISOString()
      });
    } catch (err) {
      console.error('Error logging sync:', err);
    }
  }
}

export default new CarGurusRealUploadService();
